import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';

import { SignalRService } from 'src/app/features/signal-r';
import { RootStoreState } from 'src/app/store';

import {
  OutgoingEraseSignedEndorsement,
  OutgoingSignEndorsement,
  PackageUserEndorsementImages,
  SystemEndorsementImages,
  SystemFieldStatus,
  UniqueEndorsementImage,
} from '../models';
import { EndorsementsActions } from '../store/actions';
import { Observable } from 'rxjs';

@Injectable()
export class EndorsementsService {
  private listenerConfigured = false;

  constructor(
    private readonly http: HttpClient,
    private readonly store: Store<RootStoreState.State>,
    private readonly signalRService: SignalRService
  ) { }

  public getEndorsementImages(packageUserGuid: string) {
    return this.http.get<{
      packageEndorsements: Array<PackageUserEndorsementImages>;
    }>(`endorsements/packageUsers/${packageUserGuid}`);
  }

  public getUpdatedEndorsementImage(endorsementId: number, packageUserGuid: string) {
    return this.http.get(`endorsements/${endorsementId}/image/packageUsers/${packageUserGuid}`);
  }

  public getUniqueEndorsementImages() {
    return this.http.get<{
      packageEndorsementFreeTextImages: Array<UniqueEndorsementImage>;
    }>(`endorsements/freeTextImages`);
  }

  public getSystemEndorsementImages() {
    return this.http.get<{
      packageSystemEndorsements: Array<SystemEndorsementImages>;
    }>(`endorsements/systemImages`);
  }

  public signEndorsement(signedEndorsement: OutgoingSignEndorsement) {
    return this.http.post('endorsements/signed', {
      documentEndorsementLocationId: signedEndorsement.endorsementLocationId,
      packageUserGuid: signedEndorsement.packageUserGuid,
      endorsementText: signedEndorsement.data,
      optional: signedEndorsement.optional,
    });
  }

  public eraseEndorsement(erasedEndorsement: OutgoingEraseSignedEndorsement) {
    return this.http.put('endorsements/erased', {
      documentEndorsementLocationId: erasedEndorsement.endorsementLocationId,
      packageUserGuid: erasedEndorsement.packageUserGuid,
      optional: erasedEndorsement.optional,
    });
  }

  public getSystemFieldStatus(): Observable<SystemFieldStatus> {
    return this.http.get<SystemFieldStatus>(`endorsements/system/status`);
  }

  public auditEndorsements() {
    return this.http.put(`endorsements/audit`, {});
  }

  public signSystemEndorsements(packageUserGuid: string) {
    return this.http.post(`endorsements/system/signing`, { packageUserGuid });
  }

  public signSystemEndorsementsPresign(packageUserGuid: string) {
    return this.http.post(`endorsements/system/signing`, { packageUserGuid });
  }

  public configureListeners() {
    if (!this.listenerConfigured) {
      this.listenerConfigured = true;
      this.signalRService.hubConnection.on('ConfirmSignEndorsementEvent', (Data) => {
        this.store.dispatch(
          EndorsementsActions.SetEndorsementSigned({
            payload: {
              endorsementLocationId: Number(Data.documentEndorsementLocationId),
              packageUserGuid: Data.packageUserGuid,
            },
          })
        );
      });


      this.signalRService.hubConnection.on('BroadcastRollbackEndorsementEvent', (Data) => {
        this.store.dispatch(
          EndorsementsActions.BroadcastRollbackEndorsement({
            payload: {
              endorsementLocationId: Number(Data.documentEndorsementLocationId),
              packageUserGuid: Data.packageUserGuid,
            },
          })
        );
      });

      this.signalRService.hubConnection.on('EndorsementImageUpdatedEvent', (Data) => {
        this.store.dispatch(
          EndorsementsActions.EndorsementImageUpdated({
            payload: {
              endorsementId: Data.documentEndorsementLocationId,
              packageUserGuid: Data.packageUserGuid,
            },
          })
        );
      });

      this.signalRService.hubConnection.on('EndorsementProcessingStartedEvent', (Data) => {
        this.store.dispatch(
          EndorsementsActions.EndorsementProcessingStartedEvent({
            payload: {
              endorsementLocationId: Data.documentEndorsementLocationId,
              packageUserGuid: Data.packageUserGuid,
            },
          })
        );
      });

      this.signalRService.hubConnection.on('EndorsementProcessingFinishedEvent', (Data) => {
        this.store.dispatch(
          EndorsementsActions.EndorsementProcessingFinishedEvent({
            payload: {
              endorsementLocationId: Data.documentEndorsementLocationId,
              packageUserGuid: Data.packageUserGuid,
            },
          })
        );
      });

      this.signalRService.hubConnection.on('BroadcastSystemEndorsementsAppliedEvent', () => {
        this.store.dispatch(
          EndorsementsActions.SetSystemFieldStatus({
            payload: { areApplied: true, failedToApply: false },
          })
        );
      });

      this.signalRService.hubConnection.on('BroadcastAllEndorsementsSignedEvent', () => {
        this.store.dispatch(
          EndorsementsActions.SetAllEndorsementsSigned({
            payload: true,
          })
        );
      });
    }
  }
}
